// Ory Kratos SDK Configuration for Web
import { Configuration, FrontendApi } from '@ory/client';
import axios from 'axios';

// Get Kratos URL from environment or use default
// Use same-origin path to avoid cross-domain cookie issues
export const getKratosUrl = (): string => {
  const url =
    process.env.REACT_APP_KRATOS_URL || '/.openflame/api/auth';
  // Remove trailing slash
  return url.replace(/\/$/, '');
};

// Create Ory SDK instance
export const createOrySdk = (): FrontendApi => {
  const kratosUrl = getKratosUrl();

  const configuration = new Configuration({
    basePath: kratosUrl,
    baseOptions: {
      // Required for browser flows - sends cookies with cross-origin requests
      withCredentials: true,
      timeout: 10000,
    },
  });

  return new FrontendApi(configuration);
};

// Singleton SDK instance
let sdkInstance: FrontendApi | null = null;

export const getOrySdk = (): FrontendApi => {
  if (!sdkInstance) {
    sdkInstance = createOrySdk();
  }
  return sdkInstance;
};

// Helper to handle Ory API errors - returns detailed error info
export const handleOryError = (error: any, includeDebug: boolean = false): string => {
  if (axios.isAxiosError(error)) {
    const response = error.response;
    const requestUrl = error.config?.url || 'unknown';
    const requestBaseUrl = error.config?.baseURL || '';
    const fullUrl = requestBaseUrl ? `${requestBaseUrl}${requestUrl}` : requestUrl;
    const status = response?.status || 'no response';

    // Debug info prefix
    const debugPrefix = includeDebug ? `[${status}] ${fullUrl}\n\n` : '';

    if (response?.status === 400) {
      // Validation error - extract message from UI nodes
      const ui = response.data?.ui;
      if (ui?.messages?.length > 0) {
        return debugPrefix + ui.messages.map((m: any) => m.text).join('\n');
      }
      // Check for node-level errors
      const nodeErrors = ui?.nodes
        ?.filter((n: any) => n.messages?.length > 0)
        ?.flatMap((n: any) => n.messages.map((m: any) => m.text));
      if (nodeErrors?.length > 0) {
        return debugPrefix + nodeErrors.join('\n');
      }
    }

    if (response?.status === 401) {
      return debugPrefix + 'Invalid credentials. Please try again.';
    }

    if (response?.status === 403) {
      return debugPrefix + 'Access denied. Please check your permissions.';
    }

    if (response?.status === 404) {
      return debugPrefix + `Endpoint not found: ${fullUrl}`;
    }

    if (response?.status === 410) {
      return debugPrefix + 'This link has expired. Please start over.';
    }

    if (response?.status === 422) {
      return debugPrefix + 'A browser redirect is required. Please try again.';
    }

    if (response?.data?.error?.message) {
      return debugPrefix + response.data.error.message;
    }

    // Network error or no response
    if (!response) {
      return debugPrefix + `Network error connecting to: ${fullUrl}\n${error.message}`;
    }

    return debugPrefix + `HTTP ${status}: ${error.message}`;
  }

  return error?.message || 'An unexpected error occurred. Please try again.';
};

// Extract session token from flow response
export const extractSessionToken = (response: any): string | undefined => {
  return response?.session_token;
};

// Check if flow requires browser redirect
export const flowRequiresBrowserRedirect = (error: any): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 422;
};
